import * as React from "react";
import Button from "../Button/Button";
import { Option, OptionProps } from "./Option";

type SelectOptionsProps<T> = Omit<OptionProps<T>, "item"> & {
  items: T[];
  errorMessage?: string;
  inError?: boolean;
  isDropdownOpen: boolean;
  isFilterMode?: boolean;
  isLoading?: boolean;
  isCanCreateTag?: boolean;
  keepOrder?: boolean;
  showCreateAction?: boolean;
  createActionText?: string;
  dropdownZIndex?: number;
  top?: boolean;
  singleSelect?: boolean;
  noResultMessage?: string;
  // renderer for all items
  itemsRenderer?: (items: T[], searchTerm?: string, onSelect?: (element: T) => void) => React.ReactElement;
  onCreatClick?: (text: string) => void;
  onCreateTag?: (text: string) => void;
  onRemoveSuggestion?: (item: T) => void;
  onClearSuggestions?: (items: T[]) => void;
};

function SelectOptions<T>({
  isDropdownOpen,
  items,
  selectedItems,
  readOnlyItems = [],
  idField,
  isSuggestionField,
  onClearSuggestions,
  labelField,
  searchTerm,
  onSelect,
  onRemoveSuggestion,
  isFilterMode,
  isLoading,
  errorMessage,
  color,
  keepOrder,
  children,
  isCanCreateTag,
  showCreateAction,
  createActionText,
  onCreatClick,
  onCreateTag,
  dropdownZIndex,
  itemsRenderer,
  top,
  singleSelect,
  noResultMessage
}: SelectOptionsProps<T>): React.ReactElement | null {
  const ref = React.useRef<HTMLDivElement>(null);
  const [dropdownHeight, setDropdownHeight] = React.useState<number>(0);
  const hasTerm = searchTerm?.trim()?.length > 0;
  const isShowCreateTag = isCanCreateTag && searchTerm?.trim()?.length >= 2;
  const hasError = errorMessage && errorMessage?.trim()?.length > 0;
  const allItems = React.useMemo(() => {
    let selectedElements: T[] = [];

    if (!isFilterMode && searchTerm?.trim()?.length === 0) {
      selectedElements = selectedItems;
    } else if (!keepOrder) {
      selectedElements = selectedItems.filter((s) => !!items.find((i) => s[`${idField}`] === i[`${idField}`]));
    }

    const restOfItems = !keepOrder
      ? items.filter((i) => !selectedItems.find((s) => s[`${idField}`] === i[`${idField}`]))
      : items;

    return selectedElements.concat(restOfItems);
  }, [items, selectedItems, isFilterMode, idField, searchTerm, keepOrder]);

  const isTermMatchItems = React.useMemo(
    () =>
      !!selectedItems.find((e) => e[`${labelField}`] === searchTerm) ||
      !!items.find((e) => e[`${labelField}`] === searchTerm),
    [selectedItems, items, labelField, searchTerm]
  );

  React.useEffect(() => {
    if (ref?.current && top && isDropdownOpen) {
      const el = ref?.current;
      const dropdown = el?.closest(".dropdown");
      const height = (dropdown as HTMLElement)?.offsetHeight;
      if (height) {
        setDropdownHeight(height);
      }
    }
  }, [ref?.current, top, isDropdownOpen])

  const handleOnCreateClick = (): void => onCreatClick?.(searchTerm);
  const handleOnCreateTag = (): void => onCreateTag?.(searchTerm);
  const handleOnClearAll = (): void => {
    onClearSuggestions?.(allItems?.filter(i => i[`${isSuggestionField}`]))
  };



  return isDropdownOpen ? (
    <>
      <div ref={ref} className="bg-lvl1 shadow-lg w-100" style={{ position: "absolute", right: 0, zIndex: dropdownZIndex, bottom: top? `${dropdownHeight}px`: undefined }}>
        <div className="p-0" style={{ maxHeight: "220px", overflowY: "auto" }}>
          {!hasError &&
            (isFilterMode || (!isFilterMode && !isLoading)) &&
            !isShowCreateTag &&
            hasTerm &&
            items?.length === 0 && <div className="dropdown-item">{noResultMessage ?? "No results found. Try another search."}</div>}
          {!hasError && !isFilterMode && (
            <>
              {isLoading && <div className="dropdown-item">Loading...</div>}
              {!isLoading && !itemsRenderer && !hasTerm && selectedItems?.length === 0 && items.length === 0 && (
                <div className="dropdown-item">Start typing to display results</div>
              )}
            </>
          )}
          {!hasError && !isLoading && isShowCreateTag && !isTermMatchItems && (
            <div
              style={{ cursor: "pointer" }}
              data-testid="create-tag"
              onClick={handleOnCreateTag}
              className="d-flex align-items-center list-group-item border-0 mt-0 list-group-item-action list-group-item-primary"
            >
              <div className="w-100">{searchTerm}</div>
              <span className="text-link">Create</span>
            </div>
          )}
          {hasError && <div className="dropdown-item">{errorMessage}</div>}
          {!isLoading && !hasError && (
            <div>
              {itemsRenderer && itemsRenderer(items, searchTerm, (item: T) => onSelect(item, false, false))}
              {!itemsRenderer && (
                <>
                  {!selectedItems?.length && !searchTerm && !allItems?.filter(i => !i[`${isSuggestionField}`])?.length && allItems?.filter(i => i[`${isSuggestionField}`])?.length > 0 && (
                    <div className="d-flex align-items-center justify-content-between px-3 pt-3 pb-2" >
                      <div className="font-weight-medium">Recent searches</div>
                      <button className="btn btn-flat-secondary" onClick={handleOnClearAll}>Clear all</button>
                    </div>
                  )}
                  {allItems.map((item, index) => (
                    <Option<T>
                      key={index}
                      item={item}
                      searchTerm={searchTerm}
                      selectedItems={selectedItems}
                      readOnlyItems={readOnlyItems}
                      idField={idField}
                      labelField={labelField}
                      isSuggestionField={isSuggestionField}
                      onSelect={onSelect}
                      color={color}
                      onRemoveSuggestion={onRemoveSuggestion}
                      singleSelect={singleSelect}
                    >
                      {children}
                    </Option>
                  ))}
                </>
              )}
            </div>
          )}
        </div>
        {showCreateAction && hasTerm && (
          <div className="border-top d-flex justify-content-between align-items-baseline px-3 py-2">
            <div>{searchTerm}</div>
            <Button btnType="link" onClick={handleOnCreateClick}>
              {createActionText}
            </Button>
          </div>
        )}
      </div>
    </>
  ) : null;
}

export default SelectOptions;
